import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api';
import LocationPicker, { locationLabel } from '../components/LocationPicker';
import KeywordChart from './KeywordChart';
import AdditionalKeywords from './AdditionalKeywords';
import GscKeywords24h from './GscKeywords24h';

const LABEL_COLORS = {
  'מוצר': '#3B82F6',
  'שירות': '#8B5CF6',
  'בלוג': '#22C55E',
  'מיקומי': '#F59E0B',
  'מותג': '#EC4899',
  'עמוד נחיתה': '#06B6D4',
};

function getLabelColor(label) {
  return LABEL_COLORS[label] || '#6B7280';
}

// מעצב חותמת זמן UTC של בדיקת המיקומים האחרונה לתצוגה בעברית
function fmtCheckTime(s) {
  if (!s) return null;
  try {
    var iso = /[Zz]|[+-]\d\d:?\d\d$/.test(s) ? s : s + 'Z';
    return new Date(iso).toLocaleString('he-IL', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
  } catch (e) { return null; }
}

// מחזיר את החלק שאחרי הדומיין (path + query) כדי לזהות איזה עמוד זה
function urlPath(u) {
  try {
    var parsed = new URL(u);
    return (parsed.pathname || '/') + (parsed.search || '');
  } catch (e) {
    return u;
  }
}

export default function SiteKeywords() {
  const { id } = useParams();
  const [keywords, setKeywords] = useState([]);
  const [site, setSite] = useState(null);
  const [newKeywords, setNewKeywords] = useState('');
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [updatingVol, setUpdatingVol] = useState(false);
  const [adding, setAdding] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [message, setMessage] = useState(null);
  const [sortBy, setSortBy] = useState('latest_desktop');
  const [sortDir, setSortDir] = useState('asc');
  const [selectedKw, setSelectedKw] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [filterStatus, setFilterStatus] = useState('');
  const [filterLabel, setFilterLabel] = useState('');
  const [filterStarred, setFilterStarred] = useState(false);
  const [allLabels, setAllLabels] = useState([]);
  const [showLabelInput, setShowLabelInput] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [addStatus, setAddStatus] = useState('active');
  const [addStarred, setAddStarred] = useState(false);
  const [addLabel, setAddLabel] = useState('');
  const [addLocation, setAddLocation] = useState(null);
  const [showLocationInput, setShowLocationInput] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const kwData = await api.getKeywords(id);
      setKeywords(kwData.keywords);
      try { setSite(await api.getSite(id)); } catch (e) { /* non-fatal */ }
      const labelData = await api.getSiteLabels(id);
      setAllLabels(labelData.labels || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleAddKeywords() {
    const lines = newKeywords.split('\n').map(l => l.trim()).filter(l => l);
    if (!lines.length) return;
    setAdding(true);
    try {
      const loc = addLocation ? { location_code: addLocation.code, location_name: addLocation.name } : {};
      const result = await api.addKeywords(id, lines.map(k => Object.assign({ keyword: k }, loc)));
      if (result.added > 0 && (addStatus !== 'active' || addStarred || addLabel)) {
        var freshKws = await api.getKeywords(id);
        var addedIds = freshKws.keywords.filter(function(kw) { return lines.indexOf(kw.keyword) >= 0; }).map(function(kw) { return kw.id; });
        if (addedIds.length > 0) {
          var updateData = { keyword_ids: addedIds };
          if (addStatus !== 'active') updateData.status = addStatus;
          if (addStarred) updateData.is_starred = true;
          if (addLabel) updateData.add_labels = [addLabel];
          await api.bulkUpdateKeywords(updateData);
        }
      }
      setMessage('נוספו ' + result.added + ' ביטויים' + (result.skipped > 0 ? ' (' + result.skipped + ' כפולים)' : ''));
      setNewKeywords(''); setShowAdd(false); setAddStatus('active'); setAddStarred(false); setAddLabel(''); setAddLocation(null);
      await loadData();
    } catch (err) { setMessage('שגיאה: ' + err.message); }
    setAdding(false);
    setTimeout(() => setMessage(null), 3000);
  }

  async function handleCheckRankings() {
    setChecking(true);
    setMessage('בודק מיקומים... זה יכול לקחת עד דקה');
    try {
      const result = await api.triggerRankCheck(id);
      setMessage('נבדקו ' + result.checked + ' ביטויים בהצלחה');
      await loadData();
    } catch (err) { setMessage('שגיאה: ' + err.message); }
    setChecking(false);
    setTimeout(() => setMessage(null), 5000);
  }

  async function handleUpdateVolumes() {
    setUpdatingVol(true);
    setMessage('מעדכן נפחים... זה יכול לקחת זמן');
    try {
      const res = await api.updateVolumes(id);
      setMessage('עודכנו ' + res.updated + ' נפחים מתוך ' + res.total);
      await loadData();
    } catch (err) { setMessage('שגיאה: ' + err.message); }
    setUpdatingVol(false);
    setTimeout(() => setMessage(null), 5000);
  }

  async function handleDelete(kwId, kwText) {
    if (!window.confirm('למחוק את "' + kwText + '"?')) return;
    try { await api.deleteKeyword(kwId); await loadData(); }
    catch (err) { alert('שגיאה: ' + err.message); }
  }

  async function bulkAction(action, extra) {
    var ids = Array.from(selected);
    if (!ids.length) return;
    try {
      if (action === 'delete') {
        if (!window.confirm('למחוק ' + ids.length + ' ביטויים?')) return;
        await api.bulkDeleteKeywords(ids);
      } else {
        await api.bulkUpdateKeywords(Object.assign({ keyword_ids: ids }, extra));
      }
      setSelected(new Set());
      await loadData();
    } catch (err) { alert('שגיאה: ' + err.message); }
  }

  async function addLabelToSelected() {
    if (!newLabel.trim()) return;
    await bulkAction('update', { add_labels: [newLabel.trim()] });
    setNewLabel('');
    setShowLabelInput(false);
  }

  function handleSort(field) {
    if (sortBy === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('asc'); }
  }

  function getFiltered() {
    var list = keywords;
    if (filterStatus) list = list.filter(function(k) { return (k.status || 'active') === filterStatus; });
    if (filterStarred) list = list.filter(function(k) { return k.is_starred; });
    if (filterLabel) list = list.filter(function(k) { return (k.labels || []).indexOf(filterLabel) >= 0; });
    return list;
  }

  function getSorted(list) {
    return list.slice().sort(function(a, b) {
      var aVal = a[sortBy], bVal = b[sortBy];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'string') return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }

  function toggleSelect(kwId) {
    var s = new Set(selected);
    if (s.has(kwId)) s.delete(kwId); else s.add(kwId);
    setSelected(s);
  }

  function toggleSelectAll(filtered) {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(function(k) { return k.id; })));
  }

  if (loading) return React.createElement('div', {className:'loading'}, 'טוען...');

  var filtered = getFiltered();
  var sorted = getSorted(filtered);

  // קלפי הסיכום משקפים את התוצאות המסוננות (כולל הכוכב). gating נשאר על קיום ביטויים בכלל.
  var hasKeywords = keywords.length > 0;
  var total = filtered.length;
  var top3 = filtered.filter(function(k) { return k.latest_desktop && k.latest_desktop <= 3; }).length;
  var top10 = filtered.filter(function(k) { return k.latest_desktop && k.latest_desktop <= 10; }).length;
  var ranked = filtered.filter(function(k) { return k.latest_desktop; }).length;
  var notRanked = total - ranked;
  var allSelected = sorted.length > 0 && selected.size === sorted.length;

  function posClass(pos) {
    if (!pos) return 'beyond';
    if (pos <= 3) return 'top3';
    if (pos <= 10) return 'top10';
    if (pos <= 20) return 'top20';
    return 'beyond';
  }

  function changeCell(val) {
    if (val == null) return React.createElement('span', {style:{color:'var(--text-muted)'}}, '-');
    var cls = val > 0 ? 'up' : val < 0 ? 'down' : 'flat';
    var txt = val > 0 ? '▲ +' + val : val < 0 ? '▼ ' + val : '0';
    return React.createElement('span', {className:'change-badge ' + cls}, txt);
  }

  return React.createElement(React.Fragment, null,
    // Summary Cards
    React.createElement('div', {className:'site-overview-header', style:{gridTemplateColumns:'repeat(5, 1fr)'}},
      React.createElement('div', {className:'site-metric-card animate-in stagger-1'},
        React.createElement('div', {className:'label'}, 'מילים'),
        React.createElement('div', {className:'value-row'}, React.createElement('div', {className:'value', style:{color:'var(--accent)'}}, total))
      ),
      React.createElement('div', {className:'site-metric-card animate-in stagger-2'},
        React.createElement('div', {className:'label'}, 'Top 3'),
        React.createElement('div', {className:'value-row'}, React.createElement('div', {className:'value', style:{color:'var(--green)'}}, top3))
      ),
      React.createElement('div', {className:'site-metric-card animate-in stagger-3'},
        React.createElement('div', {className:'label'}, 'עמוד ראשון'),
        React.createElement('div', {className:'value-row'}, React.createElement('div', {className:'value', style:{color:'var(--cyan, #06B6D4)'}}, top10))
      ),
      React.createElement('div', {className:'site-metric-card animate-in stagger-4'},
        React.createElement('div', {className:'label'}, 'מדורגים'),
        React.createElement('div', {className:'value-row'}, React.createElement('div', {className:'value', style:{color:'var(--yellow)'}}, ranked))
      ),
      React.createElement('div', {className:'site-metric-card animate-in stagger-5'},
        React.createElement('div', {className:'label'}, 'ללא מיקום'),
        React.createElement('div', {className:'value-row'}, React.createElement('div', {className:'value', style:{color:'var(--red)'}}, notRanked))
      )
    ),

    // Actions Row
    React.createElement('div', {className:'filter-row', style:{marginBottom:'12px', flexWrap:'wrap', gap:'8px'}},
      React.createElement('button', {className:'btn-sm primary', onClick:handleCheckRankings, disabled:checking || !hasKeywords}, checking ? 'בודק...' : 'בדיקת מיקומים'),
      React.createElement('span', {style:{fontSize:'12px', color:'var(--text-secondary)', alignSelf:'center', whiteSpace:'nowrap'}, title:'מתעדכן בכל בדיקת מיקומים (ידנית או מתוזמנת)'},
        (site && site.last_rank_check_at) ? ('בדיקה אחרונה: ' + fmtCheckTime(site.last_rank_check_at)) : 'טרם בוצעה בדיקה'
      ),
      site && site.location_name && React.createElement('span', {style:{fontSize:'12px', color:'var(--text-secondary)', alignSelf:'center', whiteSpace:'nowrap'},
        title:'מיקום ברירת המחדל לבדיקה (הגדרות האתר). ביטוי עם 📍 נבדק מהמיקום שלו.'}, '📍 ' + locationLabel(site.location_name)),
      React.createElement('button', {className:'btn-sm', onClick:function() { setShowAdd(!showAdd); }}, '+ הוספת ביטויים'),
      React.createElement('button', {className:'btn-sm', onClick:handleUpdateVolumes, disabled:updatingVol}, updatingVol ? 'מעדכן...' : 'עדכן נפחים'),
      React.createElement('div', {style:{flex:1}}),
      React.createElement('select', {value:filterStatus, onChange:function(e) { setFilterStatus(e.target.value); },
        style:{padding:'4px 8px',borderRadius:'6px',border:'1px solid var(--border)',background:'var(--surface)',color:'var(--text)',fontSize:'12px',fontFamily:'inherit'}},
        React.createElement('option', {value:''}, 'כל הסטטוסים'),
        React.createElement('option', {value:'active'}, 'פעיל'),
        React.createElement('option', {value:'paused'}, 'מושהה')
      ),
      allLabels.length > 0 && React.createElement('select', {value:filterLabel, onChange:function(e) { setFilterLabel(e.target.value); },
        style:{padding:'4px 8px',borderRadius:'6px',border:'1px solid var(--border)',background:'var(--surface)',color:'var(--text)',fontSize:'12px',fontFamily:'inherit'}},
        React.createElement('option', {value:''}, 'כל התוויות'),
        allLabels.map(function(l) { return React.createElement('option', {key:l, value:l}, l); })
      ),
      React.createElement('button', {className:'btn-sm' + (filterStarred ? ' primary' : ''), onClick:function() { setFilterStarred(!filterStarred); },
        style:{fontSize:'14px',padding:'4px 10px'}}, filterStarred ? '★' : '☆')
    ),

    // Message
    message && React.createElement('div', {style:{padding:'10px 14px',borderRadius:'8px',marginBottom:'12px',background:'rgba(59,130,246,0.1)',color:'var(--accent)',fontSize:'13px'}}, message),

    // Add Keywords Panel
    showAdd && React.createElement('div', {className:'section-card', style:{marginBottom:'16px',padding:'16px'}},
      React.createElement('textarea', {value:newKeywords, onChange:function(e) { setNewKeywords(e.target.value); },
        placeholder:'הכנס ביטויים - כל שורה ביטוי חדש', rows:4,
        style:{width:'100%',padding:'10px',borderRadius:'6px',border:'1px solid var(--border)',background:'var(--bg)',color:'var(--text)',fontSize:'13px',fontFamily:'inherit',resize:'vertical'}}),
      React.createElement('div', {style:{display:'flex',gap:'10px',alignItems:'center',marginTop:'10px',flexWrap:'wrap'}},
        React.createElement('select', {value:addStatus, onChange:function(e){setAddStatus(e.target.value);},
          style:{padding:'6px 8px',borderRadius:'6px',border:'1px solid var(--border)',background:'var(--bg)',color:'var(--text)',fontSize:'12px',fontFamily:'inherit'}},
          React.createElement('option', {value:'active'}, 'פעיל'),
          React.createElement('option', {value:'paused'}, 'מושהה')
        ),
        React.createElement('button', {className:'btn-sm' + (addStarred ? ' primary' : ''), onClick:function(){setAddStarred(!addStarred);},
          style:{fontSize:'14px',padding:'4px 10px'}}, addStarred ? '★' : '☆'),
        React.createElement('input', {value:addLabel, onChange:function(e){setAddLabel(e.target.value);}, placeholder:'תווית...',
          style:{padding:'6px 8px',borderRadius:'6px',border:'1px solid var(--border)',background:'var(--bg)',color:'var(--text)',fontSize:'12px',width:'100px',fontFamily:'inherit'}}),
        allLabels.length > 0 && allLabels.slice(0,4).map(function(l) {
          return React.createElement('button', {key:l, className:'btn-sm', onClick:function(){setAddLabel(l);}, style:{fontSize:'11px',padding:'2px 6px'}}, l);
        }),
        React.createElement(LocationPicker, {siteId:id, value: addLocation && addLocation.name,
          onSelect: setAddLocation, onClear: function(){ setAddLocation(null); }, clearLabel: 'ברירת מחדל',
          placeholder: '📍 מיקום: ברירת המחדל של האתר', style:{width:'260px'}}),
        React.createElement('div', {style:{flex:1}}),
        React.createElement('button', {className:'btn-sm primary', onClick:handleAddKeywords, disabled:adding}, adding ? 'מוסיף...' : 'הוספה'),
        React.createElement('button', {className:'btn-sm', onClick:function() { setShowAdd(false); }}, 'ביטול')
      )
    ),

    // Bulk Actions Bar
    selected.size > 0 && React.createElement('div', {style:{
      display:'flex', alignItems:'center', gap:'10px', padding:'10px 16px',
      background:'rgba(59,130,246,0.08)', border:'1px solid rgba(59,130,246,0.2)',
      borderRadius:'8px', marginBottom:'12px', flexWrap:'wrap'}},
      React.createElement('span', {style:{fontWeight:600,fontSize:'13px',color:'var(--accent)'}}, selected.size + ' נבחרו'),
      React.createElement('div', {style:{width:'1px',height:'20px',background:'var(--border)'}}),
      React.createElement('button', {className:'btn-sm', onClick:function() { bulkAction('update', {status:'active'}); }}, '✓ פעיל'),
      React.createElement('button', {className:'btn-sm', onClick:function() { bulkAction('update', {status:'paused'}); }}, '⏸ מושהה'),
      React.createElement('button', {className:'btn-sm', onClick:function() { bulkAction('update', {is_starred:true}); }}, '★'),
      React.createElement('button', {className:'btn-sm', onClick:function() { bulkAction('update', {is_starred:false}); }}, '☆'),
      React.createElement('div', {style:{width:'1px',height:'20px',background:'var(--border)'}}),
      showLabelInput
        ? React.createElement('div', {style:{display:'flex',gap:'4px',alignItems:'center'}},
            React.createElement('input', {value:newLabel, onChange:function(e) { setNewLabel(e.target.value); }, placeholder:'שם תווית...',
              onKeyDown:function(e) { if (e.key === 'Enter') addLabelToSelected(); },
              style:{padding:'4px 8px',borderRadius:'4px',border:'1px solid var(--border)',background:'var(--bg)',color:'var(--text)',fontSize:'12px',width:'120px',fontFamily:'inherit'}}),
            allLabels.map(function(l) {
              return React.createElement('button', {key:l, className:'btn-sm', onClick:function() { setNewLabel(l); }, style:{fontSize:'11px',padding:'2px 6px'}}, l);
            }),
            React.createElement('button', {className:'btn-sm primary', onClick:addLabelToSelected, style:{fontSize:'11px',padding:'3px 8px'}}, '+'),
            React.createElement('button', {className:'btn-sm', onClick:function() { setShowLabelInput(false); }, style:{fontSize:'11px',padding:'3px 8px'}}, '✕')
          )
        : React.createElement('button', {className:'btn-sm', onClick:function() { setShowLabelInput(true); }}, '🏷 תווית'),
      React.createElement('div', {style:{width:'1px',height:'20px',background:'var(--border)'}}),
      showLocationInput
        ? React.createElement('div', {style:{display:'flex',gap:'4px',alignItems:'center'}},
            React.createElement(LocationPicker, {siteId:id, style:{width:'240px'}, placeholder:'📍 חפש עיר / מדינה...',
              onSelect: function(loc) { setShowLocationInput(false); bulkAction('update', {location_code: loc.code, location_name: loc.name}); }}),
            React.createElement('button', {className:'btn-sm', title:'חזרה למיקום ברירת המחדל של האתר',
              onClick:function() { setShowLocationInput(false); bulkAction('update', {clear_location: true}); }, style:{fontSize:'11px',padding:'3px 8px'}}, 'ברירת מחדל'),
            React.createElement('button', {className:'btn-sm', onClick:function() { setShowLocationInput(false); }, style:{fontSize:'11px',padding:'3px 8px'}}, '✕')
          )
        : React.createElement('button', {className:'btn-sm', onClick:function() { setShowLocationInput(true); }}, '📍 מיקום'),
      React.createElement('div', {style:{flex:1}}),
      React.createElement('button', {className:'btn-sm', onClick:function() { bulkAction('delete'); },
        style:{color:'var(--red)',borderColor:'rgba(239,68,68,0.3)'}}, '🗑 מחיקה'),
      React.createElement('button', {className:'btn-sm', onClick:function() { setSelected(new Set()); }, style:{fontSize:'11px'}}, 'ביטול')
    ),

    // Keywords Table
    !hasKeywords
      ? React.createElement('div', {className:'empty-state'},
          React.createElement('h3', null, 'אין ביטויים'),
          React.createElement('p', null, 'הוסף ביטויים כדי להתחיל לעקוב אחרי מיקומים')
        )
      : React.createElement('div', {className:'section-card animate-in'},
          React.createElement('table', {className:'rankings-table'},
            React.createElement('thead', null,
              React.createElement('tr', null,
                React.createElement('th', {style:{width:'36px',textAlign:'center'}},
                  React.createElement('input', {type:'checkbox', checked:allSelected, onChange:function() { toggleSelectAll(sorted); }, style:{cursor:'pointer'}})
                ),
                [{label:'ביטוי', field:'keyword'}, {label:'נפח', field:'search_volume'}, {label:'Desktop', field:'latest_desktop'}, {label:'Mobile', field:'latest_mobile'}, {label:'התחלתי', field:'initial_position'}, {label:'הכי טוב', field:'best_position'}, {label:'שינוי שבוע', field:'change_week'}, {label:'URL מדורג', field:'url_ranked'}].map(function(col) {
                  return React.createElement('th', {key:col.field, onClick:function() { handleSort(col.field); },
                    style:{cursor:'pointer', color: sortBy === col.field ? 'var(--accent)' : undefined}},
                    col.label + ' ' + (sortBy === col.field ? (sortDir === 'asc' ? '▲' : '▼') : '')
                  );
                }),
                React.createElement('th', {style:{width:'30px'}})
              )
            ),
            React.createElement('tbody', null,
              sorted.map(function(kw) {
                var pos = kw.latest_desktop;
                var mob = kw.latest_mobile;
                var isExpanded = selectedKw === kw.id;
                var isChecked = selected.has(kw.id);
                var isPaused = (kw.status || 'active') === 'paused';

                var labelSpans = (kw.labels || []).map(function(label) {
                  return React.createElement('span', {key:label, style:{
                    fontSize:'10px', padding:'1px 6px', borderRadius:'8px',
                    background: getLabelColor(label) + '18',
                    color: getLabelColor(label), fontWeight:600, whiteSpace:'nowrap',
                  }}, label);
                });

                var row = React.createElement('tr', {
                  key: 'row-' + kw.id,
                  onClick: function() { setSelectedKw(isExpanded ? null : kw.id); },
                  style: {
                    cursor: 'pointer',
                    background: isChecked ? 'rgba(59,130,246,0.06)' : isExpanded ? 'rgba(59,130,246,0.08)' : undefined,
                    opacity: isPaused ? 0.5 : 1,
                  }
                },
                  React.createElement('td', {style:{textAlign:'center'}, onClick:function(e) { e.stopPropagation(); }},
                    React.createElement('input', {type:'checkbox', checked:isChecked, onChange:function() { toggleSelect(kw.id); }, style:{cursor:'pointer'}})
                  ),
                  React.createElement('td', null,
                    React.createElement('div', {style:{display:'flex',alignItems:'center',gap:'6px',flexWrap:'wrap'}},
                      kw.is_starred && React.createElement('span', {style:{color:'#F59E0B',fontSize:'14px'}, title:'מועדף'}, '★'),
                      isPaused && React.createElement('span', {style:{fontSize:'12px',color:'var(--text-muted)'}, title:'מושהה'}, '⏸'),
                      React.createElement('span', {className:'keyword-text'}, kw.keyword),
                      kw.location_name && React.createElement('span', {title:'נבדק מ: ' + kw.location_name, style:{
                        fontSize:'10px', padding:'1px 6px', borderRadius:'8px', background:'rgba(107,114,128,0.12)',
                        color:'var(--text-secondary)', whiteSpace:'nowrap', direction:'ltr'}}, '📍 ' + locationLabel(kw.location_name)),
                      labelSpans.length > 0 && labelSpans
                    )
                  ),
                  React.createElement('td', {style:{textAlign:'center',fontFamily:'var(--mono)',fontSize:'13px',color:'var(--text-secondary)'}}, kw.search_volume ? kw.search_volume.toLocaleString() : '-'),
                  React.createElement('td', null, React.createElement('span', {className:'position-badge ' + posClass(pos)}, pos == null ? '-' : pos >= 31 ? '30+' : pos)),
                  React.createElement('td', null, React.createElement('span', {className:'position-badge ' + posClass(mob)}, mob == null ? '-' : mob >= 31 ? '30+' : mob)),
                  React.createElement('td', {style:{textAlign:'center',fontFamily:'var(--mono)',fontSize:'13px',color:'var(--text-muted)'}}, kw.initial_position || '-'),
                  React.createElement('td', null, React.createElement('span', {className:'position-badge ' + posClass(kw.best_position)}, kw.best_position || '-')),
                  React.createElement('td', null, changeCell(kw.change_week)),
                  React.createElement('td', {style:{direction:'ltr',textAlign:'right',fontSize:'11px',fontFamily:'var(--mono)',maxWidth:'150px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}},
                    kw.url_ranked
                      ? React.createElement('a', {
                          href: kw.url_ranked, target:'_blank', rel:'noopener noreferrer',
                          title: kw.url_ranked, onClick: function(e) { e.stopPropagation(); },
                          style:{color:'var(--accent)', textDecoration:'none'}
                        }, urlPath(kw.url_ranked))
                      : React.createElement('span', {style:{color:'var(--text-muted)'}}, '-')
                  ),
                  React.createElement('td', null,
                    React.createElement('button', {
                      onClick:function(e) { e.stopPropagation(); handleDelete(kw.id, kw.keyword); },
                      style:{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:'14px',opacity:0.4,padding:'2px'},
                      onMouseEnter:function(e) { e.target.style.opacity=1; },
                      onMouseLeave:function(e) { e.target.style.opacity=0.4; },
                      title:'מחיקה'
                    }, '\u00D7')
                  )
                );

                var chartRow = isExpanded ? React.createElement('tr', {key:'chart-' + kw.id},
                  React.createElement('td', {colSpan:'10', style:{padding:0}},
                    React.createElement(KeywordChart, {keywordId:kw.id, keyword:kw.keyword, onClose:function() { setSelectedKw(null); }})
                  )
                ) : null;

                return React.createElement(React.Fragment, {key:kw.id}, row, chartRow);
              })
            )
          )
        ),

    // מיקום לפי Search Console ב-24 השעות האחרונות — separate table
    hasKeywords && site && site.gsc_property && React.createElement(GscKeywords24h, {siteId:id}),

    // ביטויים נוספים — separate table, manual check only
    React.createElement(AdditionalKeywords, {siteId:id})
  );
}